import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Trophy, Crown, Heart, Clock, PlayCircle, HelpCircle } from 'lucide-react'; 
import { useGameSounds } from '../../../core/hooks/useGameSounds';
import TacticalOverlay from '../../../components/shared/TacticalOverlay';
import ConfidenceSlider from '../../../components/shared/ConfidenceSlider';

const BattleRoyaleGame = ({ gameData, onComplete }) => {
  const { playSuccess, playError, playExtinguish, playWin } = useGameSounds();
  
  // Safe destructuring with defaults
  const { 
    questions = [], 
    id = 'game_final_exam', 
    preGameBriefing, 
    failureLogic 
  } = gameData || {};

  // --- STATE ---
  const [stage, setStage] = useState('briefing'); 
  const [confidencePre, setConfidencePre] = useState(5);
  
  // Game Logic
  const [activeQuestions, setActiveQuestions] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [lives, setLives] = useState(1);
  const [bots, setBots] = useState(Array(99).fill('active')); 
  const [feedback, setFeedback] = useState(null); 
  const [activeIntervention, setActiveIntervention] = useState(null);

  // --- ADD THIS REF WITH THE OTHER REFS ---
  const timeoutsRef = useRef([]);

  // --- ADD THIS CLEANUP EFFECT ---
  useEffect(() => {
    return () => {
      // Clear all pending timeouts on unmount to prevent memory leaks
      timeoutsRef.current.forEach(clearTimeout);
    };
  }, []);

  // --- 1. DIFFICULTY CALCULATOR ---
  const calculateDifficulty = (conf) => {
      if (conf >= 8) return { qCount: 8, lives: 0, label: "EXPERT (Sudden Death)" };
      if (conf >= 5) return { qCount: 6, lives: 1, label: "STANDARD (1 Mistake)" };
      return { qCount: 5, lives: 2, label: "TRAINING (2 Mistakes)" };
  };

  // --- 2. INITIALIZATION ---
  useEffect(() => {
    // If no data, don't set stage, let the error render
    if (!gameData) return;

    if (preGameBriefing) {
        setStage('briefing');
    } else {
        setStage('pre');
    }
  }, [preGameBriefing, gameData]);

  // --- 3. START GAME ---
  const handleStartGame = () => {
      const settings = calculateDifficulty(confidencePre);
      const pool = questions.length > 0 ? questions : []; 
      
      let array = [...pool];
      for (let i = array.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [array[i], array[j]] = [array[j], array[i]];
      }
      
      // Also shuffle the options for each selected question to prevent positional bias
      const shuffledQuestions = array.slice(0, settings.qCount).map(q => {
          let opts = [...q.options];
          for (let i = opts.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [opts[i], opts[j]] = [opts[j], opts[i]];
          }
          return { ...q, options: opts };
      });
      
      setActiveQuestions(shuffledQuestions);
      setLives(settings.lives);
      setQuestionIndex(0);
      setBots(Array(99).fill('active')); 
      setStage('playing');
  };

  // --- 4. SIMULATION ---
  const runSimulation = (survivalRate) => {
    setBots(prevBots => prevBots.map(status => {
      if (status === 'eliminated') return 'eliminated';
      return Math.random() > survivalRate ? 'eliminated' : 'active';
    }));
  };

  // --- 5. GAMEPLAY LOGIC ---
  const handleOptionClick = (option) => {
    if (feedback) return; 
    const currentQ = activeQuestions[questionIndex];

    if (option.correct) {
      playSuccess();
      setFeedback({ type: 'success', text: "Correct! You survived this round." });
      runSimulation(currentQ.survivalRate || 0.5);

      const timerId = setTimeout(() => {
        setFeedback(null);
        if (questionIndex + 1 < activeQuestions.length) {
          setQuestionIndex(prev => prev + 1);
        } else {
          setStage('won');
          playWin();
        }
      }, 1500); 
      timeoutsRef.current.push(timerId);

    } else {
      if (lives > 0) {
          playError();
          setLives(prev => prev - 1);
          setFeedback({ type: 'warning', text: "Mistake! Life burned." });
          
          const timerId = setTimeout(() => {
              setFeedback(null);
              if (questionIndex + 1 < activeQuestions.length) {
                  setQuestionIndex(prev => prev + 1);
              } else {
                  setStage('won');
                  playWin();
              }
          }, 1500);
          timeoutsRef.current.push(timerId);
      } else {
          playExtinguish();
          setFeedback({ type: 'error', text: "Eliminated." });
          
          const timerId = setTimeout(() => {
              const logic = failureLogic?.['eliminated'] || { title: "Eliminated", script: "You were cut.", videoUrl: null };
              setActiveIntervention(logic);
              setStage('intervention');
          }, 1000);
          timeoutsRef.current.push(timerId);
      }
    }
  };

  const handleRetry = () => {
      setFeedback(null);
      setActiveIntervention(null);
      handleStartGame(); 
  };

  // ERROR TRAP
  if (!gameData) return <div className="p-10 text-center text-red-500 font-bold border-2 border-red-200 rounded-xl bg-red-50">Error: Game Data Failed to Load</div>;

  const activeBots = bots.filter(b => b === 'active').length;
  const survivors = activeBots + 1; 

  const CandidateGrid = () => (
    <div className="flex flex-wrap justify-center gap-1 p-4 bg-gray-900 rounded-xl mb-6 max-w-md mx-auto shadow-inner h-32 overflow-hidden content-start">
      {/* Retain Framer Motion ONLY for the primary user node */}
      <motion.div className="w-3 h-3 rounded-full border-2 bg-blue-500 border-blue-300 shadow-[0_0_10px_blue] z-10" initial={{ scale: 0 }} animate={{ scale: 1.5 }} />
      
      {/* Convert bots to standard CSS transitions to prevent DOM animation bloat */}
      {bots.map((status, i) => (
        <div
          key={i}
          className={`w-2 h-2 rounded-full transition-all duration-500 ease-out ${
            status === 'active' 
              ? 'bg-gray-400 scale-100 opacity-60' 
              : 'bg-red-500 scale-50 opacity-10'
          }`}
        />
      ))}
    </div>
  );

  return (
    // FIX: Added min-h-[600px] to prevent collapse during absolute positioning phases
    <div className="h-full min-h-[600px] flex flex-col bg-white rounded-xl overflow-hidden border border-gray-200 relative shadow-lg">
      
      <AnimatePresence>
        {stage === 'briefing' && preGameBriefing && (
            <div className="absolute inset-0 z-[100] bg-black">
                <TacticalOverlay type="briefing" data={preGameBriefing} onContinue={() => setStage('pre')} />
            </div>
        )}
        {stage === 'intervention' && activeIntervention && (
            <div className="absolute inset-0 z-[100] bg-black">
                <TacticalOverlay type="intervention" data={activeIntervention} onRetry={handleRetry} />
            </div>
        )}
      </AnimatePresence>

      {stage === 'pre' && (
          <motion.div key="pre" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex-grow flex items-center justify-center p-6 bg-white z-50 absolute inset-0">
            <div className="max-w-md w-full">
              <ConfidenceSlider value={confidencePre} onChange={setConfidencePre} title="Baseline Assessment" subtitle="How ready are you for the Final Interview?" />
              <div className="mt-6 p-4 bg-gray-50 rounded-xl text-center border border-gray-200">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Gauntlet Difficulty: <span className="text-blue-600">{calculateDifficulty(confidencePre).label}</span></p>
                  <div className="flex justify-center gap-4 text-sm font-medium text-gray-700">
                      <span className="flex items-center gap-1"><Clock size={16}/> {calculateDifficulty(confidencePre).qCount} Rounds</span>
                      <span className="flex items-center gap-1"><Heart size={16}/> {calculateDifficulty(confidencePre).lives} Lives</span>
                  </div>
              </div>
              <button onClick={handleStartGame} className="mt-8 w-full bg-blue-600 text-white font-black py-4 rounded-2xl shadow-lg flex items-center justify-center gap-2 hover:bg-blue-700 transition transform hover:scale-105">
                <PlayCircle /> ENTER THE ARENA
              </button>
            </div>
          </motion.div>
      )}

      {stage === 'playing' && (
        <>
            {/* STANDARD HUD HEADER WITH BRIEFING RECALL */}
            <div className="bg-white p-4 border-b flex justify-between items-center shadow-sm z-10 w-full">
              <div className="flex items-center gap-3">
                  <button 
                      onClick={() => setStage('briefing')} 
                      className="p-1.5 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-500 transition-colors"
                      title="Review Mission Briefing"
                  >
                      <HelpCircle size={16} />
                  </button>
                  <div className="hidden sm:flex flex-col">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Active Session</span>
                      <span className="text-sm font-bold text-gray-800">{gameData?.title || 'The Gauntlet'}</span>
                  </div>
              </div>
              <div className="flex items-center gap-4">
                  <div className="flex items-center text-red-600 font-bold">
                      <Users className="w-5 h-5 mr-2" />
                      <span>{survivors} / 100 Left</span>
                  </div>
                  <div className="flex items-center gap-1 text-gray-600 text-sm font-bold">
                      <Heart className={`w-4 h-4 ${lives <= 0 ? 'text-gray-300' : 'text-red-500 fill-current'}`} />
                      <span>{lives}</span>
                  </div>
              </div>
            </div>

            <div className="bg-gray-800 p-4 shadow-inner relative">
                <CandidateGrid />
                <div className="absolute bottom-2 right-4 text-[10px] text-gray-500 uppercase font-bold tracking-widest">Live Feed</div>
            </div>

            <div className="flex-grow p-6 flex flex-col justify-center bg-white relative">
                <h3 className="text-xl font-bold text-gray-800 mb-6 leading-snug">
                    {activeQuestions[questionIndex]?.text}
                </h3>

                <div className="space-y-3">
                    {activeQuestions[questionIndex]?.options.map((opt) => (
                        <button
                            key={opt.id}
                            onClick={() => handleOptionClick(opt)}
                            disabled={!!feedback}
                            className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 font-medium
                            ${feedback 
                                ? (opt.correct ? 'bg-green-50 border-green-500 text-green-800' : 'opacity-50 border-gray-200') 
                                : 'bg-white border-gray-200 hover:border-blue-400 hover:bg-blue-50 hover:shadow-md text-gray-700'
                            }
                            `}
                        >
                            {opt.text}
                        </button>
                    ))}
                </div>
                
                <AnimatePresence>
                    {feedback && (
                        <motion.div
                            initial={{ y: 50, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 50, opacity: 0 }}
                            className={`absolute bottom-0 left-0 right-0 p-4 text-center font-bold text-white 
                                ${feedback.type === 'success' ? 'bg-green-600' : feedback.type === 'warning' ? 'bg-orange-500' : 'bg-red-600'}`}
                        >
                            {feedback.text}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </>
      )}

      {stage === 'won' && (
        <div className="h-full flex flex-col items-center justify-center bg-slate-50 rounded-xl p-8 text-center animate-fade-in absolute inset-0 z-50">
            <div className="mb-6 p-6 rounded-full bg-yellow-100">
               {survivors === 1 ? <Crown className="w-20 h-20 text-yellow-600" /> : <Trophy className="w-20 h-20 text-yellow-600" />}
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">You Made the Cut!</h2>
            <p className="text-gray-600 mb-8">
               {survivors === 1 
               ? "Incredible. You were the ONLY candidate to pass the gauntlet." 
               : `You and ${survivors - 1} others passed the screening.`}
            </p>
            <button onClick={() => onComplete(id)} className="bg-green-600 text-white font-bold py-4 px-10 rounded-xl shadow-lg hover:bg-green-700 transition">
                Accept Offer
            </button>
        </div>
      )}
    </div>
  );
};

export default BattleRoyaleGame;